import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from './../../../services/firebase';
import { PencilIcon, TrashIcon, PaperAirplaneIcon, CheckCircleIcon, HeartIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';

interface GreetingMessage {
  id: string;
  eventID: string;
  userName: string;
  userPhone: string;
  viewerId: string;
  message: string;
  timestamp: any;
  isEdited?: boolean;
}

interface GreetingsMessageBoardProps {
  eventID: string;
  currentUserName: string;
  currentUserPhone: string;
  viewerId: string;
}

export default function GreetingsMessageBoard({ eventID, currentUserName, currentUserPhone, viewerId }: GreetingsMessageBoardProps) {
  const [messages, setMessages] = useState<GreetingMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [editingMessage, setEditingMessage] = useState<GreetingMessage | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [userMessage, setUserMessage] = useState<GreetingMessage | null>(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);

  // Fetch messages and check if current user has already posted
  useEffect(() => {
    if (!eventID) {
      console.log('❌ No eventID provided');
      return;
    }



    const q = query(
      collection(db, 'greetingsMessages'),
      where('eventID', '==', eventID),
      orderBy('timestamp', 'desc')
    );



    const unsubscribe = onSnapshot(q,
      (querySnapshot) => {

        const messagesData: GreetingMessage[] = [];
        let currentUserMessage: GreetingMessage | null = null;

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          console.log('📨 Raw document data:', data);
          console.log('🆔 Document ID:', doc.id);

          const messageData: GreetingMessage = {
            id: doc.id,
            eventID: data.eventID,
            userName: data.userName,
            userPhone: data.userPhone,
            viewerId: data.viewerId,
            message: data.message,
            timestamp: data.timestamp,
            isEdited: data.isEdited || false
          };

          messagesData.push(messageData);


          // Check if this message belongs to current user using viewerId
          if (messageData.viewerId === viewerId) {
            currentUserMessage = messageData;

          }
        });



        setMessages(messagesData);
        setUserMessage(currentUserMessage);
        setLoading(false);
      },
      (error) => {

        setLoading(false);
      }
    );

    return () => {

      unsubscribe();
    };
  }, [eventID, viewerId]);

  // Separate effect to handle pre-filling the message input
  useEffect(() => {
    if (userMessage && !editingMessage) {
      setNewMessage(userMessage.message);
    } else if (!userMessage) {
      setNewMessage('');
    }
  }, [userMessage, editingMessage]);

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !eventID || !currentUserName || !currentUserPhone || !viewerId) {

      return;
    }

    setIsSubmitting(true);

    try {
      if (userMessage) {
        // Update existing message

        const messageRef = doc(db, 'greetingsMessages', userMessage.id);
        await updateDoc(messageRef, {
          message: newMessage.trim(),
          timestamp: new Date(),
          isEdited: true
        });
        setEditingMessage(null);
        showSuccess('Message updated successfully!');
      } else {
        // Add new message

        const newMessageData = {
          eventID,
          userName: currentUserName,
          userPhone: currentUserPhone,
          viewerId: viewerId,
          message: newMessage.trim(),
          timestamp: new Date(),
          isEdited: false
        };



        await addDoc(collection(db, 'greetingsMessages'), newMessageData);
        showSuccess('Message posted successfully!');
      }

      setNewMessage('');
    } catch (error) {
      console.error('Error saving message:', error);
      showSuccess('Error saving message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (messageId: string) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;

    try {
      await deleteDoc(doc(db, 'greetingsMessages', messageId));
      showSuccess('Message deleted successfully!');
    } catch (error) {
      console.error('Error deleting message:', error);
      showSuccess('Error deleting message. Please try again.');
    }
  };

  const startEditing = (message: GreetingMessage) => {
    setEditingMessage(message);
    setNewMessage(message.message);
  };

  const cancelEditing = () => {
    setEditingMessage(null);
    setNewMessage(userMessage?.message || '');
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    try {
      const date = timestamp.toDate();
      const now = new Date();
      const diffInMs = now.getTime() - date.getTime();
      const diffInHours = diffInMs / (1000 * 60 * 60);

      // If less than 24 hours, show relative time
      if (diffInHours < 24) {
        if (diffInHours < 1) {
          const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
          return `${diffInMinutes}m ago`;
        }
        return `${Math.floor(diffInHours)}h ago`;
      }

      // Otherwise show date and time
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-soft p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-3 text-gray-600 font-medium">Loading warm wishes...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-2 rounded-xl">
              <ChatBubbleLeftRightIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Messages</h2>
              <p className="text-white/90 text-sm mt-1">
                Share your warm wishes for the special occasion
              </p>
            </div>
          </div>
          <div className="bg-white/20 px-3 py-1 rounded-full">
            <span className="text-white text-sm font-medium">{messages.length}</span>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className={`mx-6 mt-4 p-4 rounded-xl text-sm ${successMessage.includes('Error')
            ? 'bg-red-50 text-red-700 border border-red-200 shadow-sm'
            : 'bg-green-50 text-green-700 border border-green-200 shadow-sm'
          }`}>
          <div className="flex items-center">
            {successMessage.includes('Error') ? (
              <span className="text-red-500 mr-3">⚠️</span>
            ) : (
              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
            )}
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Messages List - Multi-column grid layout */}
      <div className="max-h-[600px] overflow-y-auto p-6 bg-gray-50/50">
        {messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="bg-gradient-to-r from-purple-100 to-pink-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <HeartIcon className="w-8 h-8 text-purple-400" />
            </div>
            <p className="text-lg font-semibold text-gray-600 mb-2">No wishes yet</p>
            <p className="text-gray-500">Be the first to share your warm wishes!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`relative p-4 rounded-xl transition-all duration-300 transform hover:scale-[1.02] min-h-[180px] flex flex-col ${message.viewerId === viewerId
                    ? 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 shadow-soft'
                    : 'bg-white border border-gray-200 shadow-soft hover:shadow-gentle'
                  } ${editingMessage?.id === message.id ? 'ring-2 ring-purple-500 ring-opacity-50' : ''
                  }`}
              >
                {/* Message header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-sm ${message.viewerId === viewerId
                        ? 'bg-gradient-to-r from-blue-500 to-indigo-500'
                        : 'bg-gradient-to-r from-purple-500 to-pink-500'
                      }`}>
                      {message.userName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <span className={`font-semibold text-sm ${message.viewerId === viewerId ? 'text-blue-800' : 'text-gray-800'
                        }`}>
                        {message.userName}
                      </span>
                      {message.viewerId === viewerId && (
                        <span className="block text-xs text-blue-600 font-medium mt-0.5">Your wish</span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <span className="text-xs text-gray-500 bg-white/80 px-2 py-1 rounded-full shadow-xs">
                      {formatTimestamp(message.timestamp)}
                    </span>
                    {message.isEdited && (
                      <span className="text-gray-400 text-xs mt-1">edited</span>
                    )}
                  </div>
                </div>

                {/* Message content */}
                <div className="flex-1 mb-3">
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {message.message}
                  </p>
                </div>

                {/* Action buttons */}
                {message.viewerId === viewerId && (
                  <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 mt-auto">
                    <button
                      onClick={() => startEditing(message)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors px-2 py-1 rounded-lg hover:bg-blue-50 font-medium"
                    >
                      <PencilIcon className="w-3 h-3" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(message.id)}
                      className="flex items-center gap-1 text-xs text-red-600 hover:text-red-800 transition-colors px-2 py-1 rounded-lg hover:bg-red-50 font-medium"
                    >
                      <TrashIcon className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                )}

                {/* Decorative corner */}
                <div className={`absolute top-0 right-0 w-6 h-6 rounded-bl-xl ${message.viewerId === viewerId ? 'bg-blue-200' : 'bg-purple-200'
                  }`}></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Message Input Form */}
      <div className="border-t border-gray-200 p-6 bg-white">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={
                userMessage
                  ? "Update your warm wishes..."
                  : "Write your warm wishes and blessings for this special occasion..."
              }
              rows={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 resize-none transition-all duration-200 text-gray-700 placeholder-gray-500 text-base"
              maxLength={500}
              disabled={!viewerId}
            />
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-gray-500 font-medium">
                {newMessage.length}/500 characters
              </span>
              {!viewerId && (
                <span className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-1 rounded-full">
                  Please complete your details to share wishes
                </span>
              )}
              {userMessage && !editingMessage && viewerId && (
                <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-1 rounded-full">
                  You can edit your wish above
                </span>
              )}
            </div>
          </div>

          {viewerId && (
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600 font-medium">
                {userMessage ? (
                  editingMessage ? (
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></span>
                      Editing your wish...
                    </span>
                  ) : (
                    <span className="text-purple-600">✨ You've already shared your warm wishes</span>
                  )
                ) : (
                  <span className="text-gray-600">💫 Share your blessings with everyone</span>
                )}
              </div>

              <div className="flex gap-3">
                {editingMessage && (
                  <button
                    type="button"
                    onClick={cancelEditing}
                    className="px-5 py-2.5 text-sm text-gray-600 hover:text-gray-800 transition-colors border border-gray-300 rounded-xl hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={!newMessage.trim() || isSubmitting || !viewerId}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg font-medium"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Sending...
                    </>
                  ) : editingMessage ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4" />
                      Update Wish
                    </>
                  ) : userMessage ? (
                    <>
                      <PencilIcon className="w-4 h-4" />
                      Update Wish
                    </>
                  ) : (
                    <>
                      <PaperAirplaneIcon className="w-4 h-4" />
                      Share Your Wish
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}